import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListSystemRolesQuery,
  useListTenantRolesQuery,
  useCreateTenantRoleMutation,
  useUpdateTenantRoleMutation,
  useDeleteTenantRoleMutation,
  TenantRole,
} from '@/redux/internal-services/onboardingApi';
import { useIsTenantAdmin } from '@/hooks/useIsTenantAdmin';

const PermissionChip: React.FC<{ perm: string }> = ({ perm }) => (
  <span className="inline-block bg-purple-50 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full border border-purple-200">
    {perm}
  </span>
);

/** Lock icon shown next to the Tenant Admin checkbox and chip labels. */
const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={className} aria-hidden="true">
    <path d="M11 7V5a3 3 0 10-6 0v2H4a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1h-1zm-4-2a1 1 0 112 0v2H7V5zm1 6a1 1 0 110-2 1 1 0 010 2z" />
  </svg>
);

const RolesPage: React.FC = () => {
  const { t } = useTranslation();
  const isTenantAdmin = useIsTenantAdmin();
  const { data: sysData, isLoading: sysLoading } = useListSystemRolesQuery();
  const { data: tenData, isLoading: tenLoading, refetch } = useListTenantRolesQuery();
  const [createRole, { isLoading: creating }] = useCreateTenantRoleMutation();
  const [updateRole] = useUpdateTenantRoleMutation();
  const [deleteRole] = useDeleteTenantRoleMutation();

  const systemRoles = sysData?.data ?? [];
  const tenantRoles = tenData?.data ?? [];

  // ── Inline name editing — tracked for every role (default + custom) ────────
  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const [nameErrors, setNameErrors] = useState<Record<string, string>>({});
  const [nameSaving, setNameSaving] = useState<Record<string, boolean>>({});

  // Initialise editing names once tenant roles load; add new rows without resetting existing edits.
  useEffect(() => {
    if (!tenantRoles.length) return;
    setEditingNames((prev) => {
      const next = { ...prev };
      for (const r of tenantRoles) {
        if (!(r.tenant_role_id in next)) {
          next[r.tenant_role_id] = r.name;
        }
      }
      return next;
    });
  }, [tenantRoles]);

  const handleNameSave = async (role: TenantRole) => {
    const trimmed = (editingNames[role.tenant_role_id] ?? '').trim();
    if (!trimmed) return;

    // Frontend duplicate check — compare against all OTHER roles (case-insensitive, trimmed).
    const duplicate = tenantRoles.find(
      (r) =>
        r.tenant_role_id !== role.tenant_role_id &&
        r.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      setNameErrors((e) => ({ ...e, [role.tenant_role_id]: `"${duplicate.name}" already exists. Choose a different name.` }));
      return;
    }
    setNameErrors((e) => ({ ...e, [role.tenant_role_id]: '' }));
    setNameSaving((s) => ({ ...s, [role.tenant_role_id]: true }));
    try {
      await updateRole({ id: role.tenant_role_id, data: { name: trimmed } }).unwrap();
      // Reflect saved name immediately in local state (avoids full refetch flicker).
      setEditingNames((n) => ({ ...n, [role.tenant_role_id]: trimmed }));
      refetch();
    } catch (err: unknown) {
      const code = (err as { data?: { message_code?: number } })?.data?.message_code;
      const msg = code === 3708
        ? 'A role with this name already exists.'
        : 'Failed to save. Please try again.';
      setNameErrors((e) => ({ ...e, [role.tenant_role_id]: msg }));
    } finally {
      setNameSaving((s) => ({ ...s, [role.tenant_role_id]: false }));
    }
  };

  // ── Create form state ──────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newMappings, setNewMappings] = useState<string[]>([]);
  const [newVisibility, setNewVisibility] = useState(true);
  const [error, setError] = useState('');

  const tenantSystemRoleIds = systemRoles.filter((r) => r.type === 'tenant').map((r) => r.role_id);

  const handleCreate = async () => {
    if (!newName.trim() || newMappings.length === 0) {
      setError(t('admin.roleFormRequiredError'));
      return;
    }
    // Duplicate check for new role
    const trimmedNew = newName.trim();
    const dup = tenantRoles.find((r) => r.name.trim().toLowerCase() === trimmedNew.toLowerCase());
    if (dup) {
      setError(`A role named "${dup.name}" already exists.`);
      return;
    }
    setError('');
    try {
      await createRole({ name: trimmedNew, description: newDesc, system_role_mappings: newMappings, visibility: newVisibility }).unwrap();
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewMappings([]);
      refetch();
    } catch (err: unknown) {
      const code = (err as { data?: { message_code?: number } })?.data?.message_code;
      const msg = code === 3708 ? 'A role with this name already exists.' : t('admin.createRoleFailed');
      setError(msg);
    }
  };

  const toggleMapping = (roleId: string) => {
    setNewMappings((prev) => prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]);
  };

  const handleToggleVisibility = async (role: TenantRole) => {
    await updateRole({ id: role.tenant_role_id, data: { visibility: !role.visibility } }).unwrap().catch(() => { });
    refetch();
  };

  const handleDelete = async (roleId: string) => {
    if (!window.confirm(t('admin.confirmDeleteRole'))) return;
    await deleteRole(roleId).unwrap().catch(() => { });
    refetch();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ui-text">Role Management</h1>
        <p className="text-ui-text2 text-sm mt-1">Manage system roles and create custom tenant roles.</p>
      </div>

      {/* System Roles */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-ui-text mb-3">System Roles</h2>
        <p className="text-sm text-ui-text3 mb-4">Centrally managed by the platform. Permissions propagate automatically.</p>
        {sysLoading ? (
          <p className="text-ui-text3 text-sm">Loading...</p>
        ) : (
          <div className="space-y-3">
            {systemRoles.map((role) => (
              <div key={role.role_id} className="bg-ui-card border border-ui-border2 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-medium text-ui-text">{role.name}</span>
                    <span className="ml-2 text-xs text-ui-text3 bg-ui-raised px-2 py-0.5 rounded-full">{role.type}</span>
                  </div>
                </div>
                {role.description && <p className="text-sm text-ui-text2 mb-2">{role.description}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {role.permissions.map((p) => <PermissionChip key={p} perm={p} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Tenant Custom Roles */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-ui-text">Custom Roles</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-brand-dark text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition"
          >
            + Create Role
          </button>
        </div>
        <p className="text-sm text-ui-text3 mb-4">Tenant-specific roles built by combining system roles. Permissions are inherited automatically.</p>

        {tenLoading ? (
          <p className="text-ui-text3 text-sm">Loading...</p>
        ) : tenantRoles.length === 0 ? (
          <div className="bg-ui-raised border border-dashed border-ui-border rounded-xl p-8 text-center text-ui-text3 text-sm">
            No custom roles yet. Create one to customize access for your team.
          </div>
        ) : (
          <div className="space-y-3">
            {tenantRoles.map((role) => {
              const isTenantAdminRole = role.system_role_mappings.includes('tenant_admin');
              const currentName = editingNames[role.tenant_role_id] ?? role.name;
              const trimmedName = currentName.trim();
              const isDirty = trimmedName !== role.name;
              const saveDisabled = !trimmedName || nameSaving[role.tenant_role_id];

              return (
                <div key={role.tenant_role_id} className="bg-ui-card border border-ui-border2 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      {/* Inline name editor — works for ALL roles including defaults */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <input
                          type="text"
                          value={currentName}
                          onChange={(e) => {
                            setEditingNames((n) => ({ ...n, [role.tenant_role_id]: e.target.value }));
                            setNameErrors((err) => ({ ...err, [role.tenant_role_id]: '' }));
                          }}
                          className="border border-ui-border rounded-lg px-2 py-1 text-sm font-medium text-ui-text bg-ui-input focus:outline-none focus:ring-2 focus:ring-brand-dark/30 min-w-0 flex-1 max-w-xs"
                          aria-label={`Edit name for ${role.name}`}
                        />
                        {role.is_default && (
                          <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full shrink-0">Default</span>
                        )}
                        {isDirty && (
                          <button
                            type="button"
                            disabled={saveDisabled}
                            onClick={() => handleNameSave(role)}
                            className="text-xs bg-brand-dark text-white px-3 py-1 rounded-lg transition hover:bg-[#3d2aa0] disabled:opacity-40 shrink-0"
                          >
                            {nameSaving[role.tenant_role_id] ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </div>
                      {nameErrors[role.tenant_role_id] && (
                        <p className="text-xs text-red-500 mb-1">{nameErrors[role.tenant_role_id]}</p>
                      )}
                      {role.description && <p className="text-sm text-ui-text2 mb-2">{role.description}</p>}
                      <div className="flex flex-wrap gap-1.5">
                        {role.system_role_mappings.map((m) => (
                          <span key={m} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200">
                            → {m}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Visibility checkbox + delete */}
                    <div className="flex items-center gap-2 ml-auto shrink-0">
                      {/* Tenant Admin checkbox: always checked + locked for the logged-in admin */}
                      {isTenantAdminRole && isTenantAdmin ? (
                        <div className="flex items-center gap-1" title="Your Tenant Admin role is always active — it keeps your admin access.">
                          <div
                            className="w-5 h-5 rounded border-2 flex items-center justify-center opacity-60 cursor-not-allowed"
                            style={{ backgroundColor: 'rgb(var(--color-brand-dark))', borderColor: 'rgb(var(--color-brand-dark))' }}
                            aria-checked="true"
                            role="checkbox"
                            aria-label="Tenant Admin always active"
                          >
                            <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <LockIcon className="w-3.5 h-3.5 text-ui-text3" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleToggleVisibility(role)}
                          aria-checked={role.visibility}
                          role="checkbox"
                          title={role.visibility ? 'Visible — click to hide' : 'Hidden — click to show'}
                          className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-dark/30"
                          style={{
                            backgroundColor: role.visibility ? 'rgb(var(--color-brand-dark))' : 'transparent',
                            borderColor: role.visibility ? 'rgb(var(--color-brand-dark))' : '#9CA3AF',
                          }}
                        >
                          {role.visibility && (
                            <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      )}

                      {!role.is_default && (
                        <button
                          onClick={() => handleDelete(role.tenant_role_id)}
                          className="text-xs text-red-500 hover:text-red-700 border border-red-100 px-2 py-1 rounded transition"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Self-lock note below the Tenant Admin row */}
                  {isTenantAdminRole && isTenantAdmin && (
                    <p className="mt-2 text-xs text-ui-text3 flex items-center gap-1">
                      <LockIcon className="w-3 h-3 shrink-0" />
                      This role is always active for you — it keeps your admin access.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Create Role Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-4">Create Custom Role</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium text-ui-text mb-1">Role Name</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="e.g. Senior Estimator" />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-ui-text mb-1">Description (optional)</label>
              <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-ui-text mb-2">Map to System Roles (select one or more)</label>
              <div className="flex flex-wrap gap-2">
                {tenantSystemRoleIds.map((rid) => (
                  <button key={rid} type="button" onClick={() => toggleMapping(rid)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${newMappings.includes(rid) ? 'bg-brand-dark text-white' : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'}`}>
                    {rid}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={newVisibility} onChange={(e) => setNewVisibility(e.target.checked)} className="w-4 h-4 accent-purple-600" />
              <span className="text-sm text-ui-text2">Show in role selection (hide mapped system roles)</span>
            </label>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex gap-3">
              <button onClick={() => { setShowCreate(false); setError(''); }}
                className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition">
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating}
                className="flex-1 bg-brand-dark text-white py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPage;
